import React, { useState } from 'react';
import {
  Box, Container, Typography, Grid, Card, CardContent, CardActions,
  Button, CircularProgress, Alert, TextField, MenuItem, Tabs, Tab,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import TableChartIcon from '@mui/icons-material/TableChart';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { getQuarterlyReport, getTaxReport, exportCSV, exportPDF } from '../services/api';

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
const quarters = [1, 2, 3, 4];

const Reports = () => {
  const [tab, setTab] = useState(0);
  const [year, setYear] = useState(currentYear);
  const [quarter, setQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleLoadQuarterly = async () => {
    setLoading(true); setError(null); setReport(null);
    try {
      const res = await getQuarterlyReport(year, quarter);
      setReport(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load quarterly report');
    } finally { setLoading(false); }
  };

  const handleLoadTax = async () => {
    setLoading(true); setError(null); setReport(null);
    try {
      const res = await getTaxReport(year);
      setReport(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load tax report');
    } finally { setLoading(false); }
  };

  const handleExport = async (type) => {
    try {
      const res = type === 'csv' ? await exportCSV() : await exportPDF();
      const blob = new Blob([res.data], { type: type === 'csv' ? 'text/csv' : 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `portfolio-report.${type}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Failed to export ${type.toUpperCase()}`);
    }
  };

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <Box sx={{ background: 'linear-gradient(135deg, #052d50 0%, #478289 100%)', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ fontWeight: 700 }}>Reports</Typography>
          <Typography variant="h6" sx={{ opacity: 0.85, mt: 1 }}>Financial statements, tax documents, and exports</Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* Export Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6}>
            <Card sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
              <TableChartIcon sx={{ fontSize: 48, color: '#478289', mr: 2 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Export CSV</Typography>
                <Typography variant="body2" color="textSecondary">Download portfolio data as spreadsheet</Typography>
              </Box>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => handleExport('csv')}>CSV</Button>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Card sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
              <PictureAsPdfIcon sx={{ fontSize: 48, color: '#d32f2f', mr: 2 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Export PDF</Typography>
                <Typography variant="body2" color="textSecondary">Download portfolio report as PDF</Typography>
              </Box>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => handleExport('pdf')}>PDF</Button>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tab} onChange={(_, v) => { setTab(v); setReport(null); }}
            sx={{ '& .Mui-selected': { color: '#478289' }, '& .MuiTabs-indicator': { backgroundColor: '#478289' } }}>
            <Tab icon={<AssessmentIcon />} label="Quarterly Statements" iconPosition="start" />
            <Tab icon={<ReceiptIcon />} label="Tax Documents" iconPosition="start" />
          </Tabs>
        </Box>

        {/* Quarterly */}
        {tab === 0 && (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <TextField select label="Year" value={year} onChange={(e) => setYear(e.target.value)} sx={{ minWidth: 120 }}>
                  {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </TextField>
                <TextField select label="Quarter" value={quarter} onChange={(e) => setQuarter(e.target.value)} sx={{ minWidth: 120 }}>
                  {quarters.map((q) => <MenuItem key={q} value={q}>Q{q}</MenuItem>)}
                </TextField>
                <Button variant="contained" onClick={handleLoadQuarterly} disabled={loading}
                  sx={{ backgroundColor: '#478289', alignSelf: 'center' }}>
                  {loading ? <CircularProgress size={20} /> : 'Load Report'}
                </Button>
              </Box>
              {report && (
                <Box sx={{ backgroundColor: '#f5f5f5', p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Q{quarter} {year} Statement
                  </Typography>
                  {typeof report === 'object' ? (
                    Object.entries(report).map(([key, val]) => (
                      <Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
                        <strong>{key.replace(/([A-Z])/g, ' $1').trim()}:</strong>{' '}
                        {typeof val === 'number' ? val.toLocaleString() : String(val)}
                      </Typography>
                    ))
                  ) : (
                    <Typography variant="body2">{String(report)}</Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tax */}
        {tab === 1 && (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <TextField select label="Tax Year" value={year} onChange={(e) => setYear(e.target.value)} sx={{ minWidth: 120 }}>
                  {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </TextField>
                <Button variant="contained" onClick={handleLoadTax} disabled={loading}
                  sx={{ backgroundColor: '#478289', alignSelf: 'center' }}>
                  {loading ? <CircularProgress size={20} /> : 'Load Tax Documents'}
                </Button>
              </Box>
              {report && (
                <Box sx={{ backgroundColor: '#f5f5f5', p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    {year} Tax Documents
                  </Typography>
                  {typeof report === 'object' ? (
                    Object.entries(report).map(([key, val]) => (
                      <Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
                        <strong>{key.replace(/([A-Z])/g, ' $1').trim()}:</strong>{' '}
                        {typeof val === 'number' ? val.toLocaleString() : String(val)}
                      </Typography>
                    ))
                  ) : (
                    <Typography variant="body2">{String(report)}</Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </Container>
    </Box>
  );
};

export default Reports;
